import React, { useRef, useState, useLayoutEffect, useCallback } from 'react';
import styled from 'styled-components';

// components
import AudioPlayer from './AudioPlayer';
import InfoPopup from './InfoPopup';
import Hotspot from './../components/Hotspot';
import TextModule from './../components/TextModule';
import NextIcon from './../Icons/icon-next-arrow.svg?react';

// functionality
import slideAnimation from './../animations/slideAnimation';
import useWindowSize from '../hooks/useWindowSize';
import isMobile from '../utils/isMobile';

const LearnMoreHrStyled = styled.hr`
  margin-bottom: 2rem;
  margin-top: 2rem;
`;

const LearnMoreHeadingStyled = styled.h2`
  font-weight: bold;
  margin-bottom: 1rem;
`;

const LearnMoreHotspotContainerStyled = styled.div`
  &:not(:last-child) {
    margin-bottom: 1rem;
  }
`;

const InfoSlide = ({
  setSlideIsAnimating,
  isActive,
  pageReady,
  body,
  audio,
  image_alt_text,
  image_position,
  hotspots,
  mobile_hotspots,
  show_continue_cta,
  appMeta,
  nextSlide,
}) => {
  const windowSize = useWindowSize();
  const slideRef = useRef();
  const hotspotRef = useRef();
  const textRef = useRef();
  const imageRef = useRef();

  // Use a Ref for the timeline to prevent re-render loops
  const timeline = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  const beginFunction = useCallback(() => {
    setSlideIsAnimating(true);
  }, [setSlideIsAnimating]);

  const endFunction = useCallback(() => {
    setSlideIsAnimating(false);
    setHasAnimated(true);
  }, [setSlideIsAnimating]);

  // Handle Timeline Construction and Triggering
  useLayoutEffect(() => {
    let anim;
    if (isActive && pageReady && !hasAnimated) {
      anim = slideAnimation(
        textRef,
        imageRef,
        hotspotRef,
        beginFunction,
        endFunction,
        windowSize.width,
      );
      timeline.current = anim;
      timeline.current?.play();
    }

    return () => {
      if (timeline.current) {
        timeline.current.pause();
        timeline.current = null;
      }
    };
  }, [isActive, pageReady, hasAnimated, beginFunction, endFunction, windowSize.width]);

  // hotspot logic...
  let hotspotItems = [];
  let hotspotImage = '';

  if (isMobile()) {
    hotspotImage = mobile_hotspots?.image;
    hotspotItems = mobile_hotspots?.items;
  } else {
    hotspotImage = hotspots?.image;
    hotspotItems = hotspots?.items;
  }

  const classArray = () => {
    const classes = ['slide'];
    if (isActive) classes.push('slide--active');
    if (image_position === 'right') classes.push('slide--invert');
    if (image_position === 'top') classes.push('slide--column');
    return classes.join(' ');
  };

  return (
    <div ref={slideRef} className={classArray()}>
      <div className="slide__content" inert={isActive ? undefined : true}>
        <div className="slide__image">
          <div className="content imageContent">
            <div className="slide__hotspots">
              <div className="content" ref={hotspotRef}>
                {hotspotItems?.map((hotspot, index) => (
                  <Hotspot {...hotspot} key={`hotspot-${index}`} />
                ))}
              </div>
            </div>

            <div className="image" ref={imageRef}>
              <img
                className={isMobile() ? 'mobileImage' : 'desktopImage'}
                alt={image_alt_text || 'Slide Data'}
                src={hotspotImage}
              />
            </div>
          </div>
        </div>

        <div className="slide__text" ref={textRef}>
          <TextModule text={body} isActive={isActive} />

          {audio && (
            <>
              <div className="slide__audio slide__audio-desktop">
                <AudioPlayer audioFile={audio} />
              </div>
              <div className="slide__audio slide__audio-mobile">
                <AudioPlayer audioFile={audio} />
              </div>
            </>
          )}

          {!!show_continue_cta && (
            <div className="continue-cta-container">
              <button className="button continue-cta" onClick={nextSlide}>
                <span>Continue</span>
                <NextIcon />
              </button>
            </div>
          )}

          {hotspots?.items?.length > 0 && (
            <div>
              <LearnMoreHrStyled />
              <LearnMoreHeadingStyled>
                {appMeta?.learn_more_text || 'Learn More'}
              </LearnMoreHeadingStyled>

              {hotspots.items.map((hotspot, index) => (
                <LearnMoreHotspotContainerStyled key={`info-${index}`}>
                  <InfoPopup {...hotspot} />
                </LearnMoreHotspotContainerStyled>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InfoSlide;
